import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { Box, Stack } from "@mui/material";
import { Routes } from "constants/routes";
import { useActionHook } from "hooks";
import { useAuthActions, useUserActions, useViewActions } from "store/actions-context";
import { IAuthData } from "store/types";
import { getProtocol } from "utils/auth";

import { AuthCode } from "./Auth-code";
import { DefaultChoice } from "./Default-choice";
import { LoginWithCode } from "./LoginWithCode";

export type LoginStep = "loading" | "default" | "authCode" | "easyLogin";

const protocol = getProtocol();

export const Login: React.FC = () => {
  const { authAction, easyLoginAction, sessionAction } = useAuthActions();
  const { getPrivacyPolicyAction, getTermsOfServiceAction } = useUserActions();
  const { setLogoutToAnotherDeviceAction, setSidebarStateAction } = useViewActions();

  const { push, replace } = useHistory();
  const { state } = useLocation<{ enterState: { pathname: string; search?: string } } | undefined>();

  const [authData, setAuthData] = useState<IAuthData | null>(null);
  const [step, setStep] = useState<LoginStep>("default");
  const [email, setEmail] = useState<string>("");

  useEffect(() => {
    setLogoutToAnotherDeviceAction(""); //invalidate logout popup
  }, []);

  const {
    action,
    loading,
    error: errorsFromApi,
    resetError,
    setLoading
  } = useActionHook(authAction, {
    onSuccess: async (response) => {
      try {
        if (response.redirect_to) {
          const link = document.createElement("a");
          link.href = `${protocol}${response.redirect_to}`;
          return link.click();
        }

        if (response.verification_code) {
          return setStep("authCode");
        }

        if (!Object.keys(response).length) {
          setLoading(true);
          // Here we can put keys of URL queries to ignore taking user settings to queries
          // It can be reports or deals
          await sessionAction([]);

          // Check if user enter with a link, remember it and redirect after login
          if (!Object.keys(response).length && state?.enterState) {
            const { pathname, search } = state.enterState;
            setTimeout(() => replace({ pathname: pathname, ...(search && { search }) }), 100);
            return;
          }
        }
      } catch (error) {
        console.log("[Something went wrong]: ", error);
      } finally {
        setLoading(false);
        setSidebarStateAction(true);
      }
    }
  });

  const {
    data: easyLoginData,
    action: easyLogin,
    loading: easyLoginLoading
  } = useActionHook(easyLoginAction, {
    onSuccess: () => {
      setStep("easyLogin");
      setSidebarStateAction(true);
    }
  });

  const login = useCallback(
    async (data: IAuthData) => {
      try {
        await action(data);
        setAuthData(data);
      } catch (_e) {
        console.log("[Login Error]");
      }
    },
    [action]
  );

  const authWithCode = useCallback(
    async (code: string) => {
      try {
        if (authData?.email && authData.password) {
          await action({ ...authData, verification_code: code });
        } else {
          throw "There are no email and password";
        }
      } catch (error) {
        console.log("[Something went wrong]: ", error);
      }
    },
    [authData]
  );

  const authLoginWithCode = useCallback(
    async (login_code: string) => {
      try {
        if (email && login_code) {
          await action({ email, login_code });
        } else {
          throw "There are no email or login_code";
        }
      } catch (error) {
        console.log("[Something went wrong]: ", error);
      }
    },
    [email]
  );

  return (
    <Stack sx={{ bgcolor: "#f5f7f7", width: "100vw", height: "100vh" }} direction="row">
      <Box sx={{ bgcolor: "#173c4c", maxWidth: "30%", minWidth: "30%" }} />
      <Stack gap="20px" sx={{ p: "30px 25px 5px 110px", flexGrow: 1 }}>
        {step === "default" && (
          <DefaultChoice
            inProgress={easyLoginLoading || loading}
            goToForgetPass={() => push(Routes.resetPassword)}
            setEmail={setEmail}
            onEasyLogin={easyLogin}
            onSubmit={login}
            apiValidationErrors={errorsFromApi}
            resetApiErrors={resetError}
            getPrivacyPolicy={getPrivacyPolicyAction}
            getTerms={getTermsOfServiceAction}
          />
        )}
        {step === "authCode" && (
          <AuthCode onSubmit={authWithCode} goToForgetPass={() => push(Routes.resetPassword)} setLoginStep={setStep} />
        )}
        {step === "easyLogin" && (
          <LoginWithCode
            data={easyLoginData}
            resendCode={() => easyLogin(email)}
            onSubmit={authLoginWithCode}
            goBack={() => setStep("default")}
            apiValidationErrors={errorsFromApi}
            loading={loading}
          />
        )}
      </Stack>
    </Stack>
  );
};
